import type {
  SupervisorPeriodConfirmedState,
  SupervisorPeriodState,
} from '../../hooks/advisory/useSupervisorTurn';
import {
  defaultReportingPeriodV01,
  type ReportingPeriodV01,
} from '../../hooks/advisory/advisoryReportingPeriod';
import type {
  AdvisoryPeriodFactStatus,
  AdvisoryPeriodFactV01,
} from '../../hooks/advisory/useAdvisoryPeriodFact';
import type { StrategicAdvisoryDoc } from '../../redux/slices/advisory';
import { validateAdvisoryPeriodFactPath } from '../../services/advisoryService';

export interface AdvisoryPeriodFeatureEnvironment {
  periodEnabled?: string;
}

/** Bardh's company confirmation is part of the base journey; only period needs a rollout flag. */
export const isAdvisoryPeriodConfirmationEnabled = (
  environment: AdvisoryPeriodFeatureEnvironment = {
    periodEnabled: process.env.REACT_APP_ADVISORY_PERIOD_CONFIRMATION_ENABLED,
  }
): boolean => environment.periodEnabled === 'true';

export type AdvisoryPeriodView =
  | { tag: 'hidden' }
  | { tag: 'loading' }
  | { tag: 'confirmed'; period: ReportingPeriodV01 }
  | {
      tag: 'required';
      period: ReportingPeriodV01;
      defaulted: boolean;
      extractionFailed: boolean;
      acceptStoredProposal: boolean;
    }
  | { tag: 'denied' }
  | { tag: 'failed' }
  | { tag: 'invalid' };

export interface AdvisoryPeriodViewInput {
  enabled: boolean;
  userId?: string;
  projectId?: string;
  project: StrategicAdvisoryDoc | null;
  livePeriod: SupervisorPeriodState | null;
  liveConfirmation: SupervisorPeriodConfirmedState | null;
  factStatus: AdvisoryPeriodFactStatus;
  fact: AdvisoryPeriodFactV01 | null;
  currentYear?: number;
}

/** Current-turn SSE wins; the server-owned period fact restores the journey after reload. */
export function deriveAdvisoryPeriodView({
  enabled,
  userId,
  projectId,
  project,
  livePeriod,
  liveConfirmation,
  factStatus,
  fact,
  currentYear,
}: AdvisoryPeriodViewInput): AdvisoryPeriodView {
  if (!enabled || !project) return { tag: 'hidden' };
  if (project.analysis?.conversationId) return { tag: 'hidden' };

  const validLiveConfirmation =
    liveConfirmation && userId && projectId
      ? validateAdvisoryPeriodFactPath(liveConfirmation.factPath, userId, projectId)
      : null;
  if (liveConfirmation && validLiveConfirmation) {
    return { tag: 'confirmed', period: liveConfirmation.period };
  }
  if (livePeriod?.confirmed) {
    return { tag: 'confirmed', period: livePeriod.period };
  }
  if (livePeriod?.needsConfirmation) {
    return {
      tag: 'required',
      period: livePeriod.period,
      defaulted: livePeriod.defaulted,
      extractionFailed: livePeriod.extractionFailed,
      acceptStoredProposal: true,
    };
  }
  if (fact?.confirmed) {
    return { tag: 'confirmed', period: fact.period };
  }
  if (factStatus === 'ready' && fact) {
    return {
      tag: 'required',
      period: fact.period,
      defaulted: fact.defaulted,
      extractionFailed: fact.extractionFailed,
      acceptStoredProposal: true,
    };
  }
  if (factStatus === 'absent') {
    return {
      tag: 'required',
      period: defaultReportingPeriodV01(currentYear),
      defaulted: true,
      extractionFailed: false,
      acceptStoredProposal: false,
    };
  }
  if (factStatus === 'denied') return { tag: 'denied' };
  if (factStatus === 'failed') return { tag: 'failed' };
  if (factStatus === 'invalid') return { tag: 'invalid' };
  return { tag: 'loading' };
}
